import { useState } from "react";
import { sleepAPI } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Separator } from "./ui/separator";
import { toast } from "sonner";
import { Trash2, Clock, Moon, TrendingUp, Calendar, Eye, Download, Coffee, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_16338d16-af9f-4ec6-a8f3-cd6da42efe59/artifacts/7xtaj2mx_WhatsApp%20Image%202026-03-02%20at%203.01.19%20PM%20%281%29.jpeg";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SleepHistory = ({ entries, onDelete, showAll = false }) => {
  const [selectedEntry, setSelectedEntry] = useState(null);

  const getClassificationStyle = (classification) => {
    switch (classification) {
      case "Good":
        return "bg-green-100 text-green-800 border-green-200";
      case "Moderate":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "Poor":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const handleDownloadPDF = (entryId, subjectName, date) => {
    const token = localStorage.getItem('token');
    const url = `${BACKEND_URL}/api/sleep-entries/${entryId}/download-pdf?token=${token}`;
    window.open(url, '_blank');
    toast.success(`Downloading PDF report for ${date}`);
  };

  const handleViewReport = (entry) => {
    setSelectedEntry(entry);
  };

  if (entries.length === 0) {
    return (
      <Card className="card-base text-center py-12">
        <Moon className="h-12 w-12 mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">No sleep entries recorded yet.</p>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {entries.map((entry, index) => (
          <Card 
            key={entry.id} 
            className="card-base hover-lift animate-fadeIn"
            style={{ animationDelay: `${index * 50}ms` }}
            data-testid={`entry-card-${entry.id}`}
          >
            <CardContent className="p-0">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{entry.date}</p>
                      <p className="text-sm text-slate-500">{entry.subject_name}, {entry.age} years</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getClassificationStyle(entry.sleep_classification)}>
                      {entry.sleep_classification}
                    </Badge>
                  </div>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-slate-500 mb-1">
                      <Clock className="h-4 w-4" />
                      <span className="text-xs uppercase tracking-wide">Time in Bed</span>
                    </div>
                    <p className="text-lg font-semibold text-slate-900">
                      {formatDuration(entry.total_time_in_bed_minutes)}
                    </p>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-slate-500 mb-1">
                      <Moon className="h-4 w-4" />
                      <span className="text-xs uppercase tracking-wide">Sleep Time</span>
                    </div>
                    <p className="text-lg font-semibold text-slate-900">
                      {formatDuration(entry.total_sleep_time_minutes)}
                    </p>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-slate-500 mb-1">
                      <TrendingUp className="h-4 w-4" />
                      <span className="text-xs uppercase tracking-wide">Efficiency</span>
                    </div>
                    <p className={`text-lg font-semibold ${
                      entry.sleep_efficiency >= 85 ? 'text-green-600' : 
                      entry.sleep_efficiency >= 70 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {entry.sleep_efficiency}%
                    </p>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-slate-500 mb-1">
                      <span className="text-xs uppercase tracking-wide">Quality</span>
                    </div>
                    <p className="text-lg font-semibold text-slate-900">
                      {entry.quality}
                    </p>
                  </div>
                </div>

                {/* Actions Row */}
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm flex-1">
                    <div>
                      <span className="text-slate-500">Day Type:</span>
                      <span className="ml-2 text-slate-700 capitalize">{entry.day_type}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Bed Time:</span>
                      <span className="ml-2 text-slate-700">{entry.bed_time}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Wake Time:</span>
                      <span className="ml-2 text-slate-700">{entry.final_wake_time}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Nap:</span>
                      <span className="ml-2 text-slate-700">{entry.nap}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewReport(entry)}
                      data-testid={`view-report-${entry.id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Report
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadPDF(entry.id, entry.subject_name, entry.date)}
                      data-testid={`download-pdf-${entry.id}`}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                    {showAll && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-400 hover:text-red-600"
                        onClick={() => onDelete(entry.id)}
                        data-testid={`delete-entry-${entry.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Report View Modal */}
      <Dialog open={!!selectedEntry} onOpenChange={() => setSelectedEntry(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-6">
          {selectedEntry && (
            <>
              {/* Report Header - Logo on RIGHT, Text on LEFT */}
              <div className="pb-6 mb-6">
                <div className="flex items-start justify-between">
                  {/* Left side - SleepMed text */}
                  <div>
                    <h1 className="text-3xl font-bold text-[#1e3a5f]">SleepMed</h1>
                    <p className="text-slate-500 text-sm">Laboratory · Solutions · Education</p>
                  </div>
                  {/* Right side - Logo */}
                  <img 
                    src={LOGO_URL} 
                    alt="SleepMed Logo" 
                    className="h-20 w-auto"
                    data-testid="report-logo"
                  />
                </div>
                {/* Blue divider line */}
                <div className="h-1 bg-primary w-full mt-4 mb-6"></div>
                {/* Title centered */}
                <div className="text-center">
                  <h2 className="text-2xl font-semibold text-primary">Sleep Diary Report</h2>
                  <p className="text-slate-500 mt-1">Two-Week Sleep Diary Program</p>
                </div>
              </div>

              {/* Patient Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-primary mb-3">Patient Information</h3>
                <div className="bg-slate-50 rounded-lg p-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Name</p>
                    <p className="font-medium">{selectedEntry.subject_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Age</p>
                    <p className="font-medium">{selectedEntry.age} years</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Date</p>
                    <p className="font-medium">{selectedEntry.date}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Day Type</p>
                    <p className="font-medium capitalize">{selectedEntry.day_type}</p>
                  </div>
                </div>
              </div>

              {/* Pre-Sleep Consumption (if any) */}
              {selectedEntry.consumption_items && 
               selectedEntry.consumption_items.length > 0 && 
               !selectedEntry.consumption_items.includes("None") && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                    <Coffee className="h-5 w-5" />
                    Pre-Sleep Consumption
                  </h3>
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Items Consumed</p>
                        <p className="font-medium text-amber-700">{selectedEntry.consumption_items.join(", ")}</p>
                      </div>
                      {selectedEntry.consumption_timing && (
                        <div>
                          <p className="text-sm text-slate-500">Timing</p>
                          <p className="font-medium">{selectedEntry.consumption_timing}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Sleep Diary Responses */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-primary mb-3">Sleep Diary Responses</h3>
                <div className="bg-slate-50 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-primary text-white">
                        <th className="text-left p-3 text-sm font-medium">Question</th>
                        <th className="text-left p-3 text-sm font-medium">Response</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      <tr><td className="p-3 text-sm text-slate-600">Bed Time</td><td className="p-3 text-sm font-medium">{selectedEntry.bed_time}</td></tr>
                      <tr className="bg-white"><td className="p-3 text-sm text-slate-600">Time to Fall Asleep</td><td className="p-3 text-sm font-medium">{selectedEntry.sleep_latency}</td></tr>
                      <tr><td className="p-3 text-sm text-slate-600">Night Awakenings</td><td className="p-3 text-sm font-medium">{selectedEntry.wake_count}</td></tr>
                      <tr className="bg-white"><td className="p-3 text-sm text-slate-600">Awakening Duration</td><td className="p-3 text-sm font-medium">{selectedEntry.wake_duration}</td></tr>
                      <tr><td className="p-3 text-sm text-slate-600">Final Wake Time</td><td className="p-3 text-sm font-medium">{selectedEntry.final_wake_time}</td></tr>
                      <tr className="bg-white"><td className="p-3 text-sm text-slate-600">Got Out of Bed</td><td className="p-3 text-sm font-medium">{selectedEntry.get_up_time}</td></tr>
                      <tr><td className="p-3 text-sm text-slate-600">Daytime Nap</td><td className="p-3 text-sm font-medium">{selectedEntry.nap}</td></tr>
                      <tr className="bg-white"><td className="p-3 text-sm text-slate-600">Sleep Quality</td><td className="p-3 text-sm font-medium">{selectedEntry.quality}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Sleep Analysis Results - Table Format */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-primary mb-3">Sleep Analysis Results</h3>
                <div className="rounded-lg overflow-hidden border border-slate-200">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-primary text-white">
                        <th className="text-left p-4 font-semibold">Metric</th>
                        <th className="text-left p-4 font-semibold">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-slate-50">
                        <td className="p-4 text-slate-700">Total Time in Bed (TIB)</td>
                        <td className="p-4 font-medium text-slate-900">{formatDuration(selectedEntry.total_time_in_bed_minutes)}</td>
                      </tr>
                      <tr className="bg-white">
                        <td className="p-4 text-slate-700">Total Sleep Time (TST)</td>
                        <td className="p-4 font-medium text-slate-900">{formatDuration(selectedEntry.total_sleep_time_minutes)}</td>
                      </tr>
                      <tr className="bg-slate-50">
                        <td className="p-4 text-slate-700">Sleep Efficiency</td>
                        <td className={`p-4 font-bold ${
                          selectedEntry.sleep_efficiency >= 85 ? 'text-green-600' :
                          selectedEntry.sleep_efficiency >= 70 ? 'text-amber-600' : 'text-red-600'
                        }`}>{selectedEntry.sleep_efficiency}%</td>
                      </tr>
                      <tr className="bg-white">
                        <td className="p-4 text-slate-700">Classification</td>
                        <td className={`p-4 font-bold ${
                          selectedEntry.sleep_classification === 'Good' ? 'text-green-600' :
                          selectedEntry.sleep_classification === 'Moderate' ? 'text-amber-600' : 'text-red-600'
                        }`}>{selectedEntry.sleep_classification}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Footer with timestamp */}
              <div className="text-center text-sm text-slate-500 py-4">
                <p>Generated by SleepMediary</p>
                <p>Report generated on: {new Date().toLocaleString()}</p>
              </div>

              {/* Classification Legend - Text Only */}
              <div className="mb-6">
                <h4 className="font-bold text-slate-800 text-lg mb-4">Sleep Efficiency Classification:</h4>
                <div className="space-y-3 pl-4">
                  <p className="text-green-600 font-medium">Good: 85% or higher - Healthy sleep efficiency</p>
                  <p className="text-amber-600 font-medium">Moderate: 70-84% - Room for improvement</p>
                  <p className="text-red-700 font-medium">Poor: Below 70% - Consult a sleep specialist</p>
                </div>
              </div>

              {/* Download Button */}
              <div className="flex justify-end mt-6">
                <Button
                  onClick={() => handleDownloadPDF(selectedEntry.id, selectedEntry.subject_name, selectedEntry.date)}
                  className="btn-primary"
                  data-testid="modal-download-pdf"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF Report
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SleepHistory;
